// InspirationModule.jsx - Version avec support du partage
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CircularProgress, 
  Alert, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  CheckCircle,
  ArrowRight,
  MapPin,
  Languages,
  Globe
} from 'lucide-react';
import { getAuth } from 'firebase/auth';

// Services existants
import sourceService from '../CP-Sourcing/SourceService';
import synthesisService from '../CP-Search/SynthesisService';
import { useSourceSearch } from '../CP-Sourcing/useSourceSearch';
import GeoLanguageService from '../components/GeoLanguageService';

// Import des sous-modules
import InspirationSearch from './InspirationSearch';
import InspirationResults from './InspirationResults';
import InspirationSynthesis from './InspirationSynthesis';

const InspirationModule = ({ 
  userId, 
  currentCompetence, 
  onSelectCompetence, 
  userObjectives, 
  onNavigateToStep, 
  navigate: navigateProp,
  step 
}) => {
  // États
  const auth = getAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);

  const {
    competence,
    userPreferences,
    preferencesLoaded,
    searchParams: baseSearchParams,
    loading: baseLoading,
    error: baseError
  } = useSourceSearch(currentCompetence?.id, token, null, null);

  const [currentPhase, setCurrentPhase] = useState('search');
  const [geographicAnalysis, setGeographicAnalysis] = useState(null); // ✅ État local pour gérer les mises à jour géographiques
  const [inspirationParams, setInspirationParams] = useState({
    query: '',
    objective: currentCompetence?.title || '',
    languages: baseSearchParams.languages || ['en'],
    country: baseSearchParams.country || 'US',
    maxSources: 20,
    selectedProviders: ['Google', 'Perplexity', 'Aladinn'],
    providerMode: 'manual',
    preferredTypes: ['Educational', 'Media', 'Non-profit'],
    useAI: true,
    enableGeographicLocalization: true,
    localGlobalRatio: 0.6
  });

  const [sources, setSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [synthesis, setSynthesis] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [searchId, setSearchId] = useState(null); // ID de la recherche (anciennement sourceSessionId)

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [loadingStatus, setLoadingStatus] = useState({
    competence: 'success',
    synthesis: 'idle',
    resources: 'idle'
  });

  const [userInsights, setUserInsights] = useState({
    detectedCountry: null,
    optimizedLanguages: [],
    searchOptimization: null,
    sourcesFound: 0
  });

  // ✅ Ref pour éviter la réinitialisation multiple
  const lastInitializedCompetenceId = useRef(null);

  // ✅ Fonction réutilisable pour analyser et appliquer le contexte géographique
  const analyzeAndApplyGeographicContext = (objectiveText) => {
    if (!objectiveText) return false;

    console.log('🌍 Analyse du contexte géographique pour:', objectiveText);

    // Étape 1 : Détecter le contexte géographique
    const geoContext = GeoLanguageService.detectGeographicContext(objectiveText);
    setGeographicAnalysis(geoContext);

    // Étape 2 : Appliquer la configuration si contexte détecté
    if (geoContext?.hasGeographicContext && geoContext.countries?.length > 0) {
      const country = geoContext.countries[0].code;
      const countryInfo = GeoLanguageService.getCountryInfo(country);
      let languages = GeoLanguageService.getRecommendedLanguages(country);

      // Prioriser la langue de l'utilisateur si elle existe
      const userLang = userPreferences?.language;
      if (userLang) {
        if (languages.includes(userLang) && languages[0] !== userLang) {
          languages = [userLang, ...languages.filter(lang => lang !== userLang)];
        } else if (!languages.includes(userLang)) {
          languages = [userLang, ...languages];
        }
      }

      // Limiter à 4 langues maximum
      languages = languages.slice(0, 4);

      console.log('✅ Configuration géographique appliquée:', country, languages);

      setInspirationParams(prev => ({
        ...prev,
        objective: objectiveText,
        country,
        languages
      }));

      setUserInsights(prev => ({
        ...prev,
        detectedCountry: {
          code: country,
          name: countryInfo?.name || geoContext.countries[0].name
        },
        optimizedLanguages: languages,
        searchOptimization: `Recherche optimisée pour ${countryInfo?.name || geoContext.countries[0].name}`
      }));

      return true;
    }

    return false;
  };

  // Handlers pour navigation harmonisée (inchangés)
  const handleNavigateToResults = () => {
    console.log('🔍 Navigation vers résultats, sources disponibles:', sources.length);
    setCurrentPhase('results');
  };

  const handleNavigateToSynthesis = () => {
    console.log('🔍 Navigation vers synthèse, synthèse disponible:', !!synthesis);
    setCurrentPhase('synthesis');
  };

  const handleNewSearchFromSynthesis = () => {
    console.log('🔄 Nouvelle recherche depuis synthèse - Reset complet');
    
    // Reset de tous les états
    setSources([]);
    setSelectedSources([]);
    setSynthesis(null);
    setMetadata({});
    setError(null);
    
    // Reset des paramètres de recherche
    setInspirationParams(prev => ({
      ...prev,
      query: '', // Reset de la requête
    }));
    
    setCurrentPhase('search');
  };

  // ✅ USEEFFECT MAÎTRE: Initialisation géographique centralisée
  useEffect(() => {
    const initializeGeographicContext = () => {
      // Garde 1: Attendre que les préférences soient chargées
      if (!preferencesLoaded) {
        console.log('⏳ En attente du chargement des préférences...');
        return;
      }

      // Garde 2: Attendre que la compétence soit disponible
      if (!currentCompetence?.id || !currentCompetence?.title) {
        console.log('⏳ En attente de la compétence...');
        return;
      }

      // Garde 3: Éviter la réinitialisation multiple pour la même compétence
      if (lastInitializedCompetenceId.current === currentCompetence.id) {
        console.log('✓ Compétence déjà initialisée:', currentCompetence.id);
        return;
      }

      console.log('🎯 Initialisation du contexte géographique pour:', currentCompetence.title);

      // Étape 1: Analyser et appliquer le contexte géographique (PRIORITÉ 1)
      const geoApplied = analyzeAndApplyGeographicContext(currentCompetence.title);

      // Étape 2: Si pas de contexte géographique, appliquer les préférences utilisateur (PRIORITÉ 2)
      if (!geoApplied && userPreferences) {
        console.log('👤 Aucun contexte géographique détecté, application des préférences utilisateur');

        setInspirationParams(prev => ({
          ...prev,
          objective: currentCompetence.title,
          country: userPreferences.country || prev.country,
          languages: userPreferences.language ? [userPreferences.language] : prev.languages,
          maxSources: userPreferences.search?.maxSources || prev.maxSources,
          enableGeographicLocalization: userPreferences.enableGeographicLocalization !== false,
          localGlobalRatio: userPreferences.localGlobalRatio || prev.localGlobalRatio
        }));

        setUserInsights(prev => ({
          ...prev,
          optimizedLanguages: userPreferences.language ? [userPreferences.language] : prev.optimizedLanguages
        }));

        console.log('✅ Préférences utilisateur appliquées');
      }

      // Marquer cette compétence comme initialisée
      lastInitializedCompetenceId.current = currentCompetence.id;
      console.log('✅ Initialisation terminée pour:', currentCompetence.id);
    };

    initializeGeographicContext();
  }, [currentCompetence?.id, currentCompetence?.title, preferencesLoaded, userPreferences]);

  useEffect(() => {
    const getToken = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const idToken = await user.getIdToken(true);
          setToken(idToken);
        } else {
          setError('Veuillez vous connecter pour accéder à cette fonctionnalité.');
        }
      } catch (err) {
        console.error('InspirationModule: Erreur lors de l\'obtention du token:', err);
        setError('Erreur d\'authentification. Veuillez vous reconnecter.');
      }
    };
    
    getToken();
  }, [auth]);

  useEffect(() => {
    if (currentCompetence?.title) {
      setInspirationParams(prev => ({
        ...prev,
        objective: currentCompetence.title
      }));
    }
  }, [currentCompetence]);

  // Handler pour la mise à jour de l'objectif
  const handleObjectiveUpdate = async (newObjective) => {
    try {
      console.log('🎯 Mise à jour de l\'objectif:', newObjective);

      // ✅ Utiliser la fonction réutilisable pour analyser et appliquer le contexte
      const geoApplied = analyzeAndApplyGeographicContext(newObjective);

      // Si aucun contexte géographique détecté, juste mettre à jour l'objectif
      if (!geoApplied) {
        setInspirationParams(prev => ({
          ...prev,
          objective: newObjective
        }));
        console.log('✅ Objectif mis à jour sans contexte géographique');
      }

      // Mettre à jour currentCompetence si disponible
      if (currentCompetence && currentCompetence.id) {
        // Utiliser le service de gestion des compétences pour mettre à jour dans Firebase
        const { getFirestore, doc, updateDoc } = await import('firebase/firestore');
        const db = getFirestore();

        const competenceRef = doc(db, 'users', userId, 'competences', currentCompetence.id);
        await updateDoc(competenceRef, {
          searchObjective: newObjective,
          updatedAt: new Date().toISOString()
        });

        console.log('✅ Objectif mis à jour dans Firebase');

        // ✅ NE PAS appeler onSelectCompetence ici pour éviter le rechargement
        // La mise à jour Firebase suffit, et le composant réagira via les props React
        // onSelectCompetence doit être réservé aux vrais changements de compétence (sélection d'une autre compétence)
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de l\'objectif:', error);
      throw error;
    }
  };

  // Chargement des données existantes - MODIFIÉ pour stocker sourceSessionId
  useEffect(() => {
    const loadExistingData = async () => {
      if (!currentCompetence || !userId || !token) return;

      setInitialLoading(true);
      
      try {
        console.log('🚀 InspirationModule - Chargement via synthesisService.loadExistingData()');
        
        const result = await synthesisService.loadExistingData(
          userId, 
          currentCompetence.id, 
          token
        );
        
        if (result.success) {
          // Charger les sources
          if (result.sources && result.sources.length > 0) {
            console.log('📦 Sources trouvées:', result.sources.length);
            
            setSources(result.sources);
            setSelectedSources(result.sources);
            
            // Stocker le searchId si disponible dans les métadonnées
            if (result.sources[0]?.sessionId) {
              setSearchId(result.sources[0].sessionId);
              console.log('🔑 SearchId stocké:', result.sources[0].sessionId);
            }
            
            setMetadata({
              title: `Sources d'inspiration trouvées`,
              category: 'inspiration',
              count: result.sources.length,
              timestamp: result.sources[0]?.createdAt,
              fromSavedSession: true,
              sessionId: result.sources[0]?.sessionId
            });
            
            setUserInsights(prev => ({
              ...prev,
              sourcesFound: result.sources.length
            }));
          }
          
          // Charger synthèse (toute synthèse pour cet objectif)
          if (result.synthesis) {
            console.log('✨ Synthèse trouvée pour cet objectif');
            setSynthesis(result.synthesis);
          }
          
          console.log('✅ Données chargées, reste en phase search pour permettre la navigation');
          
        } else {
          console.log('🔭 Aucune donnée trouvée');
        }
        
      } catch (error) {
        console.error('⚠ Erreur lors du chargement:', error);
        setError('Erreur lors du chargement des données existantes');
      } finally {
        setInitialLoading(false);
      }
    };

    if (preferencesLoaded) {
      loadExistingData();
    }
  }, [currentCompetence, userId, token, preferencesLoaded]);

  // Recherche
  const handleInspirationSearch = async (searchParameters) => {
    if (!currentCompetence || !userId || !token) {
      setError('Paramètres manquants pour la recherche');
      return;
    }

    setLoading(true);
    setError(null);
    setSources([]);
    setMetadata({});

    try {
      console.log('🔍 InspirationModule - Recherche via sourceService');
      console.log('📋 Paramètres de recherche reçus:', {
        objective: searchParameters.objective,
        query: searchParameters.query,
        country: searchParameters.country,
        languages: searchParameters.languages
      });

      const searchParams = {
        userId,
        competenceId: currentCompetence.id,
        userObjective: currentCompetence.title,
        query: searchParameters.query.trim()
          ? `${searchParameters.query}`
          : `${searchParameters.objective}`,

        languages: searchParameters.languages || ['fr', 'en', 'es'],
        country: searchParameters.country || 'US',
        maxSources: searchParameters.maxSources,
        selectedProviders: searchParameters.selectedProviders,
        preferredSourceTypes: searchParameters.preferredTypes,
        useAI: searchParameters.useAI,
        enableGeographicLocalization: searchParameters.enableGeographicLocalization,
        localGlobalRatio: searchParameters.localGlobalRatio,

        searchContext: 'inspiration'
      };

      console.log('🚀 Paramètres envoyés au backend:', {
        query: searchParams.query,
        country: searchParams.country,
        languages: searchParams.languages
      });

      const result = await sourceService.getRecommendedSources(searchParams, token);

      if (result && result.success && result.sources && result.sources.length > 0) {
        console.log(`✅ ${result.sources.length} sources d'inspiration trouvées`);
        
        setSources(result.sources);
        setSelectedSources(result.sources);
        setMetadata({
          ...result.metadata,
          title: `Sources d'inspiration trouvées`,
          category: 'inspiration',
          count: result.sources.length,
          searchQuery: searchParameters.query
        });
        
        // Sauvegarde automatique
        console.log('💾 Sauvegarde automatique');
        const saveRequest = {
          userId,
          competenceId: currentCompetence.id,
          userObjective: searchParams.userObjective,
          query: searchParams.query,
          sources: result.sources,
          category: 'inspiration',
          providers: searchParams.selectedProviders,
          metadata: {
            competenceId: currentCompetence.id,
            searchContext: 'inspiration',
            inspirationSearch: true
          }
        };
        
        try {
          const saveResult = await sourceService.saveSources(saveRequest, token);
          if (saveResult.success) {
            console.log('✅ Sources sauvegardées dans "recherches"');
            // Stocker le sourceId retourné par saveSources
            if (saveResult.sourceId) {
              setSearchId(saveResult.sourceId);
              console.log('🔑 SearchId stocké depuis sauvegarde:', saveResult.sourceId);
            }
          }
        } catch (saveError) {
          console.error('⚠ Erreur sauvegarde:', saveError);
        }
        
        setUserInsights(prev => ({
          ...prev,
          sourcesFound: result.sources.length
        }));
        
        setCurrentPhase('results');
        
      } else {
        setError('Aucune source d\'inspiration trouvée');
      }
    } catch (err) {
      console.error('⚠ Erreur lors de la recherche:', err);
      setError(err.message || 'Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  // Génération synthèse (inchangée)
  const handleGenerateSynthesis = async (isRegeneration = false) => {
    if (selectedSources.length === 0) {
      setError('Veuillez sélectionner au moins une source pour générer la synthèse');
      return;
    }

    try {
      setGenerating(true);
      setLoadingStatus({
        competence: 'success',
        synthesis: 'loading',
        resources: 'loading'
      });
      
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non authentifié');
      
      const token = await user.getIdToken(true);
      
      console.log('📄 InspirationModule - Génération synthèse');
      
      // ✅ CORRECTION : Récupérer le query original depuis les métadonnées
      const originalQuery = metadata.searchQuery || inspirationParams.query || '';
      
      // 🔍 DEBUG - Vérifier les paramètres avant génération
      console.log('🔍 DEBUG - Paramètres avant génération synthèse:', {
        query: inspirationParams.query,
        originalQuery: originalQuery,
        metadataSearchQuery: metadata.searchQuery,
        objective: inspirationParams.objective,
        queryTrimmed: originalQuery.trim(),
        willUseForSearchObjective: originalQuery.trim() 
          ? originalQuery 
          : inspirationParams.objective
      });
      
      // ✅ S'assurer que les sources incluent toutes les métadonnées nécessaires
      const sourcesWithMetadata = selectedSources.map(source => ({
        ...source,
        // Garantir que les métadonnées du provider sont présentes
        provider: source.provider || source.searchProvider || 'unknown',
        searchProvider: source.searchProvider || source.provider,
        // Garantir que les scores de qualité sont présents
        reliabilityScore: source.reliabilityScore || null,
        popularityScore: source.popularityScore || null,
        trustScore: source.trustScore || null,
        authorityScore: source.authorityScore || null,
        // Métadonnées additionnelles
        providerMetadata: source.providerMetadata || null,
        accessibilityStatus: source.accessibilityStatus || null,
        verificationMethod: source.verificationMethod || null,
        hasUrlFallback: source.hasUrlFallback || false
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'}/api/search/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `inspiration motivation ${inspirationParams.objective} ${inspirationParams.query}`.trim(),
          sources: sourcesWithMetadata,
          userObjective: `${inspirationParams.objective}`,
          userLanguage: userPreferences?.language || 'fr',
          synthesisContext: 'inspiration',
          includeMotivationalElements: true,
          includeActionableInsights: true
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Erreur lors de la génération de la synthèse');
      }

      // ✅ CORRECTION : Utiliser originalQuery pour title et searchObjective
      const synthesisForSave = {
        title: originalQuery.trim() 
          ? `${originalQuery}` 
          : `${inspirationParams.objective}`,
        searchObjective: originalQuery.trim() 
          ? `${originalQuery}` 
          : `${inspirationParams.objective}`,
        content: data.synthesis,
        summary: data.synthesis.substring(0, 300) + (data.synthesis.length > 300 ? '...' : ''),
        language: userPreferences?.language || 'fr',
        category: 'inspiration',
        isInspirational: true,
        query: originalQuery,
        userObjective: inspirationParams.objective
      };
      
      // 📦 DEBUG - Vérifier l'objet synthesisForSave créé
      console.log('📦 DEBUG - Objet synthesisForSave créé:', {
        title: synthesisForSave.title,
        searchObjective: synthesisForSave.searchObjective,
        query: synthesisForSave.query,
        userObjective: synthesisForSave.userObjective
      });
      
      console.log('💾 Sauvegarde via synthesisService.saveSynthesis()');
      
      const saveResult = await synthesisService.saveSynthesis(
        userId,
        currentCompetence.id,
        synthesisForSave,
        selectedSources
      );
      
      if (saveResult.success) {
        console.log('✅ Synthèse sauvegardée dans collection "syntheses"');
      } else {
        console.warn('⚠️ Erreur sauvegarde synthèse:', saveResult.error);
      }
      
      const interfaceSynthesis = {
        title: "Synthèse de votre recherche d'inspiration",
        extract: data.synthesis,
        content: data.synthesis,
        url: "#",
        content_type: "synthesis",
        source: "Assistant IA - Inspiration",
        language: userPreferences?.language || 'fr',
        type: "synthesis",
        category: "inspiration",
        datePublished: new Date().toISOString(),
        relevanceScore: 100,
        id: saveResult.synthesisId || `inspiration-${Date.now()}`,
        sourcesCount: selectedSources.length,
        hasRealSynthesis: true,
        isInspirational: true,
        savedInSynthesesCollection: saveResult.success
      };
      
      setSynthesis(interfaceSynthesis);
      setCurrentPhase('synthesis');
      
      setLoadingStatus({
        competence: 'success',
        synthesis: 'success',
        resources: 'success'
      });
      
    } catch (error) {
      console.error('⚠ Erreur génération synthèse:', error);
      setError(error.message || 'Erreur lors de la génération de la synthèse');
      
      setLoadingStatus({
        competence: 'success',
        synthesis: 'error',
        resources: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  // Handler pour supprimer une source
  const handleDeleteSource = async (sourceToDelete) => {
    if (!searchId || !token || !currentCompetence) {
      setError('Impossible de supprimer la source: session ID manquant');
      return;
    }

    try {
      console.log('🗑️ Suppression de la source:', sourceToDelete.title || sourceToDelete.domain);
      
      // Appeler le service pour supprimer la source
      const result = await sourceService.removeSourceFromSearchDoc(
        userId,
        searchId,
        sourceToDelete,
        token,
        currentCompetence.id
      );

      if (result.success) {
        console.log('✅ Source supprimée avec succès');
        
        // Mettre à jour les états locaux
        const newSources = sources.filter(source => 
          source.url !== sourceToDelete.url && source.domain !== sourceToDelete.domain
        );
        const newSelectedSources = selectedSources.filter(source => 
          source.url !== sourceToDelete.url && source.domain !== sourceToDelete.domain
        );
        
        setSources(newSources);
        setSelectedSources(newSelectedSources);
        
        // Mettre à jour les métadonnées
        setMetadata(prev => ({
          ...prev,
          count: newSources.length
        }));
        
        // Afficher un feedback
        setError(null); // Clear any previous errors
      } else {
        setError(`Erreur lors de la suppression: ${result.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression de la source:', error);
      setError(`Erreur lors de la suppression: ${error.message}`);
    }
  };

  // Handler pour ajouter une source manuellement
  const handleAddSource = async (newSource) => {
    if (!searchId || !token || !currentCompetence) {
      setError('Impossible d\'ajouter la source: session ID manquant');
      return;
    }

    try {
      console.log('➕ Ajout de source manuelle:', newSource.title || newSource.domain);
      
      // Appeler le service pour ajouter la source
      const result = await sourceService.addSourceToSearchDoc(
        userId,
        searchId,
        newSource,
        token,
        currentCompetence.id
      );

      if (result.success) {
        console.log('✅ Source ajoutée avec succès');
        
        // Mettre à jour les états locaux
        const newSources = [...sources, newSource];
        const newSelectedSources = [...selectedSources, newSource];
        
        setSources(newSources);
        setSelectedSources(newSelectedSources);
        
        // Mettre à jour les métadonnées
        setMetadata(prev => ({
          ...prev,
          count: newSources.length
        }));
        
        // Afficher un feedback
        setError(null); // Clear any previous errors
      } else {
        setError(`Erreur lors de l'ajout: ${result.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de la source:', error);
      setError(`Erreur lors de l'ajout: ${error.message}`);
    }
  };

  const handleContinueToChat = () => {
    setSaveDialogOpen(false);
    if (currentCompetence && currentCompetence.id) {
      navigate(`/chat/${currentCompetence.id}`);
    }
  };

  // UI COMPONENTS (inchangés)
  const ContinueDialog = () => (
    <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
      <DialogTitle className="flex items-center">
        <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
        Synthèse d'inspiration prête !
      </DialogTitle>
      <DialogContent>
        <p>Votre synthèse d'inspiration a été générée avec succès.</p>
        <div className="mt-3 p-3 bg-green-50 rounded-lg">
          <p className="text-green-800 text-sm">
            <span className="font-medium">Sources utilisées :</span> {selectedSources.length} •
            <span className="font-medium"> Langues :</span> {inspirationParams.languages.map(lang => 
              GeoLanguageService.getLanguageInfo(lang)?.name || lang
            ).join(', ')}
          </p>
          {userInsights.detectedCountry && (
            <p className="text-green-700 text-xs mt-1">
              📍 {userInsights.searchOptimization}
            </p>
          )}
          <p className="text-green-600 text-xs mt-1">
            ✨ Synthèse personnalisée selon vos préférences
          </p>
          {synthesis?.savedInSynthesesCollection && (
            <p className="text-purple-600 text-xs mt-1">
              📚 Synthèse sauvegardée de façon persistante
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSaveDialogOpen(false)}>Rester ici</Button>
        <Button 
          onClick={handleContinueToChat}
          variant="contained"
          className="bg-purple-600 hover:bg-purple-700"
          endIcon={<ArrowRight className="h-4 w-4" />}
        >
          Discuter avec Aladinn
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (initialLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <CircularProgress className="text-purple-600" />
            <div className="ml-3">
              <p className="text-purple-700 font-medium">
                Chargement de votre espace d'inspiration...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
      <div className="max-w-4xl mx-auto">
        <ContinueDialog />

        {/* Erreurs */}
        {(error || baseError) && (
          <Alert severity="error" className="mb-6">
            {error || baseError}
          </Alert>
        )}

        {/* Phases */}
        {currentPhase === 'search' && (
          <InspirationSearch
            searchParams={inspirationParams}
            setSearchParams={setInspirationParams}
            onSearch={handleInspirationSearch}
            loading={loading}
            error={error}
            currentCompetence={currentCompetence}
            token={token}
            userPreferences={userPreferences}
            geographicAnalysis={geographicAnalysis}
            preferencesLoaded={preferencesLoaded}
            hasExistingSources={sources.length > 0}
            hasExistingSynthesis={synthesis !== null}
            onNavigateToResults={sources.length > 0 ? handleNavigateToResults : null}
            onNavigateToSynthesis={synthesis ? handleNavigateToSynthesis : null}
            metadata={metadata}
            onObjectiveUpdate={handleObjectiveUpdate}
          />
        )}

        {currentPhase === 'results' && (
          <InspirationResults
            sources={sources}
            selectedSources={selectedSources}
            setSelectedSources={setSelectedSources}
            metadata={metadata}
            onBackToSearch={() => setCurrentPhase('search')}
            onBackToSynthesis={synthesis ? () => setCurrentPhase('synthesis') : null}
            onGenerateSynthesis={handleGenerateSynthesis}
            generating={generating}
            onDeleteSource={handleDeleteSource}
            onAddSource={handleAddSource}
          />
        )}

        {currentPhase === 'synthesis' && (
          <InspirationSynthesis
            synthesis={synthesis}
            selectedSources={selectedSources}
            metadata={metadata}
            searchParams={inspirationParams}
            loadingStatus={loadingStatus}
            onBackToSearch={() => setCurrentPhase('search')}
            onBackToResults={() => setCurrentPhase('results')}
            onContinue={() => setSaveDialogOpen(true)}
            currentCompetence={currentCompetence}
            onNewSearch={handleNewSearchFromSynthesis}
            userPreferences={userPreferences}
            geographicAnalysis={geographicAnalysis}
            // NOUVEAU: Passer userObjectives pour le partage
            userObjective={userObjectives}
          />
        )}

      </div>
    </div>
  );
};

export default InspirationModule;
